import React, { Dispatch, useMemo } from 'react';
import { Box, FormControl, FormLabel, Paper, SxProps } from '@mui/material';
import { useFileUpload } from '../../../../hooks/useFileUpload';
import { FileInfo } from '../../../../redux/general/files';
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import DocumentRow, { IRelatedDocument } from './DocumentRow';
import FileExplorerButton from './FileExplorerButton';
import { headingFontBold, vars } from '../../../../Theme';
import Documents from './Documents';
import { ICreateSupportTicketMainState } from '../CreateSupportTicketDialog/CreateSupportTicketDialog';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const paperSx: SxProps = {
    maxHeight: 400,
    overflow: 'auto',
    width: '100%',
    paddingTop: '6px',
    paddingBottom: '6.5px',
};

const paperErrorSx: SxProps = {
    border: `1px solid ${vars.errorBorderRed}`,
};

export default ({
    fileInfo,
    formLabel,
    required = false,
    buttonLabel,
    showError = false,
    acceptFileTypes,
    appendFilesInfo,
    removeFile,
    uploadedDocuments,
    allowMultiple = false,
    documentsLabel,
    onFilesSelected,
    idPrefix = '',
    hideEmptyList = false,
    maximumFileSize = undefined,
    setShowErrorState,
}: {
    fileInfo?: FileInfo;
    formLabel?: string;
    required?: boolean;
    buttonLabel: string;
    documentsLabel?: string;
    showError?: boolean;
    acceptFileTypes: string;
    appendFilesInfo: ActionCreatorWithOptionalPayload<FileInfo[], string>;
    removeFile: ActionCreatorWithOptionalPayload<string, string>;
    uploadedDocuments: IRelatedDocument[];
    allowMultiple?: boolean;
    onFilesSelected?: () => void;
    idPrefix?: string;
    hideEmptyList?: boolean;
    maximumFileSize?: number;
    setShowErrorState?: Dispatch<
        React.SetStateAction<{ [P in keyof ICreateSupportTicketMainState]?: boolean }>
    >;
}) => {
    const { onDropFile, onRemoveFile, onInputFiles } = useFileUpload({
        appendFilesInfo,
        removeFile,
    });

    const buttonColor = useMemo(() => (showError ? 'error' : 'primary'), [showError]);

    const renderOptions = useMemo(() => {
        return {
            true: (
                <>
                    {(!hideEmptyList || uploadedDocuments.length > 0) && (
                        <Documents
                            documents={uploadedDocuments}
                            title={documentsLabel}
                            removeDocument={onRemoveFile}
                        />
                    )}
                    <FileExplorerButton
                        label={buttonLabel}
                        acceptFileTypes={acceptFileTypes}
                        onFileInput={files => {
                            const filesSize =
                                files.reduce((sum, file) => sum + (file?.size ?? 0), 0) +
                                uploadedDocuments.reduce((sum, file) => sum + (file?.size ?? 0), 0);

                            if (!maximumFileSize || filesSize < maximumFileSize) {
                                onFilesSelected?.();
                                onInputFiles(files, idPrefix);
                            }

                            setShowErrorState?.(prevState => ({
                                ...prevState,
                                exceededFileSize: maximumFileSize && filesSize > maximumFileSize,
                            }));
                        }}
                        colorVariant={buttonColor}
                    />
                </>
            ),
            false: (
                <FormControl>
                    <FormLabel required={required} sx={formLabelSx}>
                        {formLabel}
                    </FormLabel>

                    <Box sx={{ display: 'flex', alignItems: 'left' }}>
                        {fileInfo ? (
                            <Paper
                                elevation={0}
                                sx={{ ...paperSx, ...(showError ? paperErrorSx : {}) }}
                            >
                                <DocumentRow
                                    document={uploadedDocuments[0]}
                                    removeDocument={onRemoveFile}
                                />
                            </Paper>
                        ) : (
                            <FileExplorerButton
                                label={buttonLabel}
                                acceptFileTypes={acceptFileTypes}
                                onFileInput={files => {
                                    const filesSize =
                                        files.reduce((sum, file) => sum + (file?.size ?? 0), 0) +
                                        uploadedDocuments.reduce(
                                            (sum, file) => sum + (file?.size ?? 0),
                                            0,
                                        );

                                    if (!maximumFileSize || filesSize < maximumFileSize) {
                                        onFilesSelected?.();
                                        onDropFile(files[0], idPrefix);
                                    }

                                    setShowErrorState?.(prevState => ({
                                        ...prevState,
                                        exceededFileSize:
                                            maximumFileSize && filesSize > maximumFileSize,
                                    }));
                                }}
                                allowMultiple={allowMultiple}
                                colorVariant={buttonColor}
                            />
                        )}
                    </Box>
                </FormControl>
            ),
        };
    }, [allowMultiple, uploadedDocuments, showError, acceptFileTypes]);

    return renderOptions[allowMultiple.toString()];
};
